import { useQueryClient } from '@tanstack/react-query'
import { graphqlClient } from '@api/config'
import { TENANT_CONTRACTS } from '@config'
import { InventoryInput, useEquipInventoryItemsMutation } from '@generated/generates'
import { useViewer } from '@hooks'
import { SlotType, useAvatarStore } from '@modules/inventory/store/avatar.store'
import { isStaging } from '@utils/isStaging'

const DEFAULT_COUNT = 1

interface Props {
  onCallback?: () => void
}

const useEquipItems = ({ onCallback }: Props) => {
  const { viewer } = useViewer()
  const { previewItems } = useAvatarStore()
  const queryClient = useQueryClient()
  const refreshInventoryItemsQuery = () => queryClient.invalidateQueries({ queryKey: ['GetInventoryItems'] })
  const refreshDNAItemsQuery = () => queryClient.invalidateQueries({ queryKey: ['GetDnaItems'] })

  const itemsToEquip = previewItems
    .filter((item) => item.isEquipped)
    .map((item) => ({
      tokenId: item?.chain?.tokenId?.toString() as string,
      itemId: item?.id,
      soulbound: item?.soulbound as boolean,
      smartContractAddress: item?.chain?.address as string,
      slotType: item.slotType as SlotType,
      count: DEFAULT_COUNT,
      uri: item.uri as string
    }))

  const payload: InventoryInput = {
    playerAddress: viewer?.walletAddress as string,
    avatarContractAddress: isStaging ? TENANT_CONTRACTS?.testnet.avatar : TENANT_CONTRACTS.mainnet.avatar,
    inventory: itemsToEquip
  }

  const { mutateAsync: equipItemsMutation, isPending: isEquippingItems } = useEquipInventoryItemsMutation(
    graphqlClient(),
    {
      onSuccess() {
        refreshInventoryItemsQuery()
        refreshDNAItemsQuery()
        onCallback && onCallback()
      }
    }
  )

  const equipItems = async () => {
    try {
      await equipItemsMutation({ payload })
    } catch (error) {
      console.error('Error equipping items: ', error)
    }
  }

  return {
    equipItems,
    isEquippingItems,
    refreshDNAItemsQuery,
    equipItemsPayload: payload
  }
}

export default useEquipItems
